<template>
  <div class="p-4">
    <div class="flex space-x-2 mb-4">
      <button
        v-can-write="$route.params.moduleId"
        @click="navigateToAddOrder"
        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        Добавить заказ
      </button>
      <button
        v-can-write="$route.params.moduleId"
        @click="fetchOrdersFromOneC"
        :disabled="importing1C"
        class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
      >
        {{ importing1C ? 'Запрос...' : 'Запросить из 1С' }}
      </button>
    </div>

    <!-- Поиск -->
    <div class="mb-6">
      <div class="relative max-w-md">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        </div>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Поиск по номеру, покупателю или ID..."
          class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200 text-sm"
        />
        <div v-if="searchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center">
          <button
            @click="clearSearch"
            class="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Состояние загрузки -->
    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Таблица заказов -->
    <template v-else>
      <template v-if="orders.length > 0">
        <table class="w-full border-collapse bg-white shadow-md">
          <thead class="bg-gray-100">
          <tr>
            <th
              v-for="column in sortableColumns"
              :key="column.key"
              class="px-4 py-2 text-left text-sm font-medium text-gray-700 cursor-pointer select-none hover:bg-gray-200"
              :aria-sort="sortBy === column.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'"
              @click="toggleSort(column.key)"
            >
              {{ column.label }}
              <span class="ml-1 text-xs" :class="sortBy === column.key ? 'text-gray-700' : 'text-gray-400'">
                {{ sortBy === column.key ? (sortDir === 'asc' ? '↑' : '↓') : '↕' }}
              </span>
            </th>
            <th class="px-4 py-2 text-left text-sm font-medium text-gray-700">Действия</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="order in orders" :key="order.id" class="border-b hover:bg-gray-50">
            <td class="px-4 py-2 text-sm text-gray-700">{{ order.id }}</td>
            <td class="px-4 py-2 text-sm text-gray-700">{{ order.number }}</td>
            <td class="px-4 py-2 text-sm text-gray-700">{{ order.company?.name || 'Не указан' }}</td>
            <td class="px-4 py-2 text-sm text-gray-700">{{ getStatusName(order.status) }}</td>
            <td class="px-4 py-2 space-x-2">
              <button
                v-can-write="$route.params.moduleId"
                @click="navigateToEditOrder(order.id)"
                class="px-2 py-1 text-sm font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Редактировать
              </button>
              <!-- Заказ на производство создаётся в модуле производства, а не в заказах:
                   право проверяем по модулю типа production, без него кнопки нет -->
              <button
                v-if="productionModuleId"
                v-can-write="productionModuleId"
                @click="openProductionOrderModal(order)"
                :disabled="isOrderFullyCovered(order)"
                :title="isOrderFullyCovered(order) ? 'Заказ уже полностью покрыт заказами на производство' : ''"
                :class="[
                  'px-2 py-1 text-sm font-medium rounded-md focus:outline-none focus:ring-2',
                  isOrderFullyCovered(order)
                    ? 'text-gray-500 bg-gray-100 cursor-not-allowed focus:ring-gray-400'
                    : 'text-green-600 bg-green-100 hover:bg-green-200 focus:ring-green-500'
                ]"
              >
                Производство
              </button>
              <button
                v-can-write="$route.params.moduleId"
                @click="confirmDelete(order.id)"
                class="px-2 py-1 text-sm font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Удалить
              </button>
            </td>
          </tr>
          </tbody>
        </table>

        <Pagination
          :current-page="pagination.currentPage"
          :last-page="pagination.lastPage"
          :total="pagination.total"
          :per-page="pagination.perPage"
          @change-page="changePage"
        />
      </template>
      <template v-else>
        <EmptyState/>
      </template>
    </template>

    <!-- Модалка создания заказа на производство -->
    <AddProductOrderModal
      :is-visible="isProductionOrderModalVisible"
      :preselected-order="selectedOrderForProduction"
      @close="closeProductionOrderModal"
      @order-created="onProductionOrderCreated"
    />

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="isConfirmModalVisible"
         class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h2 class="text-lg font-bold mb-4">Подтвердите удаление</h2>
        <p class="text-sm text-gray-700 mb-4">Вы уверены, что хотите удалить этот заказ?</p>
        <div class="flex justify-end space-x-2">
          <button
            @click="cancelDelete"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Отмена
          </button>
          <button
            @click="proceedDelete"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Удалить
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {useRouter} from 'vue-router'; // Import Vue Router
import orderStatusesMixin from '@/mixins/orderStatusesMixin';
import EmptyState from "@/components/EmptyState.vue";
import Pagination from '@/components/Pagination.vue';
import AddProductOrderModal from '@/components/production/AddProductOrderModal.vue';
import { createUrlStateManager } from '@/utils/urlStateManager';
import { useAuthStore } from '@/stores/auth';

export default {
  components: { EmptyState, Pagination, AddProductOrderModal },
  mixins: [orderStatusesMixin],
  data() {
    return {
      orders: [],
      loading: true,
      isConfirmModalVisible: false,
      orderIdToDelete: null,
      importing1C: false,
      isProductionOrderModalVisible: false,
      selectedOrderForProduction: null,
      searchQuery: '',
      // Сортировка реестра (Z-52): ключи колонок задаёт бэкенд, OrderService::SORT_COLUMNS
      sortableColumns: [
        { key: 'id', label: 'ID' },
        { key: 'number', label: 'Номер заказа' },
        { key: 'company', label: 'Покупатель' },
        { key: 'status', label: 'Статус' },
      ],
      sortBy: 'created_at',
      sortDir: 'desc',
      page: 1,
      perPage: 20,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        perPage: 20,
      },
    };
  },
  setup() {
    const router = useRouter(); // Initialize Vue Router
    return {router};
  },
  computed: {
    // Список заказов открыт по id модуля заказов, поэтому id модуля производства
    // берём из модулей пользователя по ключу типа (как в pages/production/OrderShow.vue).
    // Нет такого модуля — действие недоступно.
    productionModuleId() {
      const modules = useAuthStore().user?.modules || [];
      return modules.find(m => m.key === 'production')?.id ?? null;
    },
  },
  created() {
    createUrlStateManager(this, {
      searchQuery: { debounce: 300, urlKey: 'search' },
      page: { type: 'number', default: 1, urlKey: 'page' },
    }, {
      routeName: 'Orders',
      onUpdate: () => this.getOrders(),
    });

    this.getOrders();
  },
  methods: {
    getOrders() {
      this.loading = true;
      const params = {
        page: this.page,
        per_page: this.perPage,
        sort_by: this.sortBy,
        sort_dir: this.sortDir,
      };
      if (this.searchQuery?.trim()) {
        params.search = this.searchQuery.trim();
      }
      axios
        .post('/api/order/getOrders', params)
        .then((response) => {
          const d = response.data.data;
          this.orders = d.data || [];
          this.pagination = {
            currentPage: d.current_page,
            lastPage: d.last_page,
            total: d.total,
            perPage: d.per_page,
          };
          this.loading = false;
        })
        .catch((error) => {
          console.error(error);
          this.loading = false;
        });
    },

    changePage(page) {
      this.page = page;
      this.getOrders();
    },

    confirmDelete(id) {
      this.orderIdToDelete = id; // Сохраняем ID заказа
      this.isConfirmModalVisible = true; // Показываем модальное окно
    },

    cancelDelete() {
      this.isConfirmModalVisible = false; // Скрываем модальное окно
      this.orderIdToDelete = null; // Очищаем ID
    },

    proceedDelete() {
      if (!this.orderIdToDelete) {
        console.error('ID заказа не указан');
        return;
      }

      axios
        .post('/api/order/deleteOrder', {id: this.orderIdToDelete})
        .then(() => {
          this.getOrders(); // Обновляем список заказов
          this.cancelDelete(); // Скрываем модальное окно
        })
        .catch((error) => {
          console.error(error);
        });
    },

    // Navigate to Add Order Page
    navigateToAddOrder() {
      this.router.push({
        name: 'AddOrder',
        query: {moduleId: this.$route.params.moduleId}, // Передаем moduleId через query
      });
    },
    navigateToEditOrder(orderId) {
      this.router.push({
        name: 'EditOrder',
        params: {id: orderId},
        query: {moduleId: this.$route.params.moduleId, ...this.$route.query},
      });
    },

    clearSearch() {
      this.searchQuery = '';
    },

    /**
     * Переключает сортировку по колонке: первый клик — по возрастанию,
     * повторный по той же колонке — разворот. Страница сбрасывается на первую,
     * иначе пользователь оказывается в середине заново отсортированного списка.
     */
    toggleSort(columnKey) {
      if (this.sortBy === columnKey) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = columnKey;
        this.sortDir = 'asc';
      }
      this.page = 1;
      this.getOrders();
    },

    isOrderFullyCovered(order) {
      if (!order.products?.length || !order.production_counts) return false;
      return order.products.every(p => Number(order.production_counts[p.id] || 0) >= Number(p.count));
    },
    openProductionOrderModal(order) {
      this.selectedOrderForProduction = order;
      this.isProductionOrderModalVisible = true;
    },
    closeProductionOrderModal() {
      this.isProductionOrderModalVisible = false;
      this.selectedOrderForProduction = null;
    },
    onProductionOrderCreated(productionOrderId) {
      this.closeProductionOrderModal();
      if (productionOrderId) {
        this.$router.push({ name: 'ProductionOrderShow', params: { id: productionOrderId } });
      }
    },

    fetchOrdersFromOneC() {
      this.importing1C = true;

      axios
        .post('/api/oneC/getOrders')
        .then(() => {
          setTimeout(() => {
            this.getOrders();
            this.importing1C = false;
          }, 3000);
        })
        .catch((error) => {
          console.error('Ошибка при запросе заказов из 1С:', error);
          this.importing1C = false;
        });
    },
  },
};
</script>
